import React, { type KeyboardEvent } from 'react';
import type { Row } from '@tanstack/react-table';
import {
  buildDataTableCellParams,
  getCellContentAlignClass,
  getRowClassName,
  getStickyCellClassName,
  getStickyLeftOffset,
  getTextAlignClass,
  resolveColumnValue
} from './utils';
import { resolveCellNavigation, shouldPreserveTextCursor } from './keyboard-navigation';
import { TABLE_THEME } from '@/components/custom/erp/table-theme';
import { buildCellSelectionKey } from './cell-selection';
import type { DataTableLeafColumn } from './types';
import { Checkbox } from '@/components/ui';
import { cn } from '@/lib/utils';

const CELL_SELECTOR = '[data-erp-cell="true"]';
const FOCUSABLE_CELL_TARGET_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';
const TEXT_CURSOR_INPUT_TYPES = new Set(['', 'email', 'password', 'search', 'tel', 'text', 'url']);

const isTextCursorInput = (element: EventTarget | null): element is HTMLInputElement | HTMLTextAreaElement => {
  if (element instanceof HTMLTextAreaElement) return true;
  if (!(element instanceof HTMLInputElement)) return false;

  return TEXT_CURSOR_INPUT_TYPES.has(element.type);
};

const focusCell = (cell: HTMLElement) => {
  const focusTarget = cell.querySelector<HTMLElement>(FOCUSABLE_CELL_TARGET_SELECTOR) ?? cell;

  focusTarget.focus({ preventScroll: true });
  cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  cell.click();
};

const renderResolvedDataCell = (row: any, rowId: string, column: any, rawValue: unknown, value: unknown) => {
  if (column.renderCell) {
    return column.renderCell(buildDataTableCellParams(row, rowId, column, rawValue, value) as any);
  }

  if (value == null) return '';
  if (Array.isArray(value)) return value.join(', ');
  return String(value);
};

interface ErpDataTableBodyProps {
  leafColumns: DataTableLeafColumn[];
  renderedColumnWidths: Record<string, number>;
  leafColumnCount: number;
  visibleRows: Row<any>[];
  totalRowCount: number;
  topSpacerHeight: number;
  bottomSpacerHeight: number;
  virtualStart: number;
  shouldVirtualize: boolean;
  loading?: boolean;
  hideEmptyState?: boolean;
  disableCellTruncate?: boolean;
  rowHeight: number;
  getRowHeight?: (params: { id: string; row: any; model: any }) => number | null | undefined | 'auto';
  selectedRowId?: string;
  rowSelectionModel: string[];
  checkboxSelection: boolean;
  showIndexColumn: boolean;
  enableCellKeyboardNavigation?: boolean;
  onSelectionChange: (nextSelectionModel: string[]) => void;
  onCellClick?: (params: any) => void;
  onCellSelection?: (event: React.MouseEvent, params: any) => boolean;
  selectedCellKeys?: Set<string>;
  onCellContextMenu?: (event: React.MouseEvent, params: any) => void;
  onRowClick?: any;
  onRowDoubleClick?: any;
}

export function ErpDataTableBody({
  leafColumns,
  renderedColumnWidths,
  leafColumnCount,
  visibleRows,
  totalRowCount,
  topSpacerHeight,
  bottomSpacerHeight,
  virtualStart,
  shouldVirtualize,
  loading = false,
  hideEmptyState = false,
  disableCellTruncate = false,
  rowHeight,
  getRowHeight,
  selectedRowId,
  rowSelectionModel,
  checkboxSelection,
  showIndexColumn,
  enableCellKeyboardNavigation = false,
  onSelectionChange,
  onCellClick,
  onCellSelection,
  selectedCellKeys = new Set(),
  onCellContextMenu,
  onRowClick,
  onRowDoubleClick
}: ErpDataTableBodyProps) {
  const shouldShowLoadingSkeleton = loading && visibleRows.length === 0;
  const shouldShowEmptyState = !hideEmptyState && !loading && visibleRows.length === 0;
  const dataColumnIds = leafColumns.filter(column => !column.sticky).map(column => column.id);
  const dataColumnCount = dataColumnIds.length;

  const handleCellKeyDown = (
    event: KeyboardEvent<HTMLElement>,
    rowIndex: number,
    rowCount: number,
    columnIndex: number,
    columnCount: number
  ) => {
    if (isTextCursorInput(event.target)) {
      const shouldPreserveCursor = shouldPreserveTextCursor({
        key: event.key,
        value: event.target.value,
        selectionStart: event.target.selectionStart,
        selectionEnd: event.target.selectionEnd
      });

      if (shouldPreserveCursor) return;
    }

    const nextCellPosition = resolveCellNavigation({
      key: event.key,
      rowIndex,
      rowCount,
      columnIndex,
      columnCount
    });

    if (nextCellPosition === null) return;

    const currentCell = event.currentTarget.closest<HTMLElement>(CELL_SELECTOR);
    const table = currentCell?.closest('table');
    const nextCell = table
      ? table?.querySelector<HTMLElement>(
          `${CELL_SELECTOR}[data-erp-row-index="${nextCellPosition.rowIndex}"][data-erp-column-index="${nextCellPosition.columnIndex}"]`
        )
      : null;

    if (!nextCell) return;

    event.preventDefault();
    event.stopPropagation();
    focusCell(nextCell);
  };

  return (
    <tbody>
      {topSpacerHeight > 0 && (
        <tr aria-hidden='true'>
          <td colSpan={leafColumnCount} className='h-0 border-none p-0'>
            <div style={{ height: topSpacerHeight }} />
          </td>
        </tr>
      )}

      {shouldShowLoadingSkeleton &&
        Array.from({ length: 8 }).map((_, rowIndex) => (
          <tr key={`loading-row-${rowIndex}`} className={cn('animate-pulse', getRowClassName(false, rowIndex))}>
            {leafColumns.map((column, cellIndex) => {
              const stickyLeft = getStickyLeftOffset(column.sticky, checkboxSelection, showIndexColumn);
              const isStickyCell = Boolean(column.sticky);

              return (
                <td
                  key={`${column.id}-${rowIndex}`}
                  className={cn(
                    'border-b align-middle text-[length:var(--erp-data-table-text-size)]',
                    TABLE_THEME.bodyBorder,
                    column.sticky ? 'px-0' : 'px-2',
                    isStickyCell && cn('border-r', TABLE_THEME.bodyBorder, TABLE_THEME.surface),
                    !isStickyCell && cn('border-r', TABLE_THEME.bodyBorder)
                  )}
                  style={{
                    height: rowHeight,
                    width: renderedColumnWidths[column.id] ?? column.width,
                    minWidth: renderedColumnWidths[column.id] ?? column.width,
                    maxWidth: renderedColumnWidths[column.id] ?? column.width,
                    position: isStickyCell ? 'sticky' : undefined,
                    left: stickyLeft,
                    zIndex: isStickyCell ? 5 : undefined,
                    backgroundClip: isStickyCell ? 'padding-box' : undefined
                  }}
                >
                  {column.sticky ? (
                    <div className={cn('mx-auto h-3 w-4 rounded', TABLE_THEME.loadingPlaceholder)} />
                  ) : (
                    <div
                      className={cn('h-3 rounded', TABLE_THEME.loadingPlaceholder)}
                      style={{ width: `${Math.min(column.width - 24, 140)}px` }}
                    />
                  )}
                </td>
              );
            })}
          </tr>
        ))}

      {visibleRows.map((row, visibleRowIndex) => {
        const rowIndex = shouldVirtualize ? virtualStart + visibleRowIndex : visibleRowIndex;
        const resolvedRowId = row.id;
        const resolvedRowHeight = getRowHeight?.({ id: resolvedRowId, row: row.original, model: row.original });
        const actualRowHeight = resolvedRowHeight === 'auto' ? undefined : (resolvedRowHeight ?? rowHeight);
        const isSelected =
          selectedRowId === resolvedRowId ||
          selectedRowId === String(row.original?.uuid || '') ||
          rowSelectionModel.includes(resolvedRowId);

        return (
          <tr
            key={resolvedRowId}
            className={cn('group', getRowClassName(isSelected, rowIndex))}
            onClick={() => onRowClick?.({ id: resolvedRowId, row: row.original })}
            onDoubleClick={() =>
              (onRowDoubleClick as any)?.({ id: resolvedRowId, row: row.original }, {} as any, {} as any)
            }
          >
            {row.getVisibleCells().map(cell => {
              const meta = cell.column.columnDef.meta as DataTableLeafColumn;
              const dataColumnIndex = meta.sticky ? -1 : dataColumnIds.indexOf(meta.id);
              const stickyLeft = getStickyLeftOffset(meta.sticky, checkboxSelection, showIndexColumn);
              const isStickyCell = Boolean(meta.sticky);
              const column = meta.column as any;
              const resolvedValue = !meta.sticky
                ? resolveColumnValue(row.original, column, resolvedRowId)
                : { rawValue: undefined, value: undefined };
              const params = !meta.sticky
                ? buildDataTableCellParams(
                    row.original,
                    resolvedRowId,
                    column,
                    resolvedValue.rawValue,
                    resolvedValue.value
                  )
                : null;
              const cellClassName =
                !meta.sticky && column?.cellClassName
                  ? typeof column.cellClassName === 'function'
                    ? column.cellClassName(params)
                    : column.cellClassName
                  : '';
              const cellSelectionKey = !meta.sticky ? buildCellSelectionKey(resolvedRowId, meta.field) : '';
              const isCellSelected = selectedCellKeys.has(cellSelectionKey);

              return (
                <td
                  key={cell.id}
                  className={cn(
                    'border-b align-middle text-[length:var(--erp-data-table-text-size)]',
                    TABLE_THEME.bodyBorder,
                    meta.sticky ? 'px-0' : 'px-2',
                    isStickyCell && cn('border-r', TABLE_THEME.bodyBorder),
                    !isStickyCell && cn('border-r', TABLE_THEME.bodyBorder),
                    !isStickyCell && 'focus-within:relative focus-within:z-30',
                    meta.sticky === 'selection' && getStickyCellClassName(isSelected, rowIndex),
                    meta.sticky === 'index' && getStickyCellClassName(isSelected, rowIndex),
                    meta.sticky === 'index' && cn('text-center', TABLE_THEME.bodyIndexText),
                    !meta.sticky && getTextAlignClass(meta.column?.align),
                    isCellSelected && 'outline-inset relative z-20 bg-sky-100 outline outline-1 outline-sky-500',
                    cellClassName
                  )}
                  style={{
                    height: actualRowHeight,
                    width: renderedColumnWidths[meta.id] ?? cell.column.getSize(),
                    minWidth: renderedColumnWidths[meta.id] ?? cell.column.getSize(),
                    maxWidth: renderedColumnWidths[meta.id] ?? cell.column.getSize(),
                    position: isStickyCell ? 'sticky' : undefined,
                    left: stickyLeft,
                    zIndex: isStickyCell ? 5 : undefined,
                    backgroundClip: isStickyCell ? 'padding-box' : undefined
                  }}
                  data-erp-cell={!meta.sticky ? 'true' : undefined}
                  data-erp-row-index={!meta.sticky ? rowIndex : undefined}
                  data-erp-column-index={!meta.sticky ? dataColumnIndex : undefined}
                  tabIndex={enableCellKeyboardNavigation && !meta.sticky ? 0 : undefined}
                  onKeyDown={
                    enableCellKeyboardNavigation && !meta.sticky
                      ? event => handleCellKeyDown(event, rowIndex, totalRowCount, dataColumnIndex, dataColumnCount)
                      : undefined
                  }
                  aria-selected={isCellSelected || undefined}
                  onClick={event => {
                    if (!meta.sticky) {
                      const column = meta.column as any;
                      const { rawValue, value } = resolveColumnValue(row.original, column, resolvedRowId);
                      const params = buildDataTableCellParams(row.original, resolvedRowId, column, rawValue, value);

                      onCellSelection?.(event, params);
                      onCellClick?.(params);
                    }
                  }}
                  onContextMenu={event => {
                    if (!meta.sticky) {
                      event.preventDefault();
                      event.stopPropagation();
                      const column = meta.column as any;
                      const { rawValue, value } = resolveColumnValue(row.original, column, resolvedRowId);
                      const params = buildDataTableCellParams(row.original, resolvedRowId, column, rawValue, value);

                      if (event.ctrlKey && onCellSelection?.(event, params)) {
                        onRowClick?.({ id: resolvedRowId, row: row.original });
                        return;
                      }
                      onCellContextMenu?.(event, params);
                    }
                  }}
                >
                  <div
                    className={cn(
                      'flex h-full w-full items-center',
                      meta.sticky === 'index' && 'justify-center',
                      !meta.sticky && getCellContentAlignClass(meta.column?.align),
                      disableCellTruncate ? 'min-w-0' : 'truncate'
                    )}
                  >
                    {meta.sticky === 'selection' ? (
                      <div className='flex h-full w-full items-center justify-center'>
                        <Checkbox
                          checked={rowSelectionModel.includes(resolvedRowId)}
                          className='size-4'
                          onClick={event => event.stopPropagation()}
                          onCheckedChange={checked => {
                            const nextSelectionModel = checked
                              ? [...rowSelectionModel, resolvedRowId]
                              : rowSelectionModel.filter(id => id !== resolvedRowId);

                            onSelectionChange(Array.from(new Set(nextSelectionModel)));
                          }}
                        />
                      </div>
                    ) : meta.sticky === 'index' ? (
                      rowIndex + 1
                    ) : (
                      renderResolvedDataCell(
                        row.original,
                        resolvedRowId,
                        column,
                        resolvedValue.rawValue,
                        resolvedValue.value
                      )
                    )}
                  </div>
                </td>
              );
            })}
          </tr>
        );
      })}

      {shouldShowEmptyState && (
        <tr>
          <td
            colSpan={leafColumnCount}
            className={cn('h-24 border-b border-r text-center text-sm', TABLE_THEME.bodyBorder, TABLE_THEME.empty)}
          >
            Không dữ liệu
          </td>
        </tr>
      )}

      {bottomSpacerHeight > 0 && (
        <tr aria-hidden='true'>
          <td colSpan={leafColumnCount} className='h-0 border-none p-0'>
            <div style={{ height: bottomSpacerHeight }} />
          </td>
        </tr>
      )}
    </tbody>
  );
}
